/**
 * LogoMark: the mark-only brand piece for the tenant.
 *
 * Source, in order:
 *  1. the tenant's uploaded square mark (`tenant.logoMarkUrl`);
 *  2. the built-in mark (`/logo-mark.svg`) for an internal tenant (DrTutor);
 *  3. a monogram: the first letter of the display name, for a new tenant
 *     that has not uploaded a mark yet.
 *
 * `gradient` (default): the artwork as is (monogram: a brand-gradient tile).
 * `boxed`: the mark painted white inside a rounded brand-gradient tile.
 * `mono`:  the mark painted in `currentColor` via CSS mask.
 *
 * `size` is the rendered HEIGHT in px. The built-in mark's width follows its
 * natural 700/850 aspect; an uploaded mark is assumed square-ish and keeps
 * its own aspect.
 */

import type { CSSProperties } from "react";

import { publicUrl } from "@/lib/tenant/basePath";
import { useTenant } from "@/lib/tenant";

const ASPECT = 700 / 850;
const BUILT_IN = publicUrl("logo-mark.svg");

export type LogoMarkVariant = "gradient" | "boxed" | "mono";

export interface LogoMarkProps {
  size?: number;
  variant?: LogoMarkVariant;
  className?: string;
  style?: CSSProperties;
  /** Accessible name. Defaults to the tenant's display name. Pass "" for decorative use. */
  title?: string;
}

const maskStyle = (src: string): CSSProperties => ({
  WebkitMaskImage: `url(${src})`,
  maskImage: `url(${src})`,
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskSize: "contain",
  maskSize: "contain",
  WebkitMaskPosition: "center",
  maskPosition: "center",
});

export function LogoMark({ size = 32, variant = "gradient", className, style, title }: LogoMarkProps) {
  const tenant = useTenant();
  const name = title ?? tenant.displayName;
  const uploaded = tenant.logoMarkUrl;
  const src = uploaded ?? (tenant.isInternal ? BUILT_IN : null);
  const width = uploaded ? size : Math.round(size * ASPECT);

  if (!src) {
    const letter = (tenant.displayName.trim().charAt(0) || "T").toUpperCase();
    const tile = variant === "boxed" ? Math.round(size * 1.1) : size;
    return (
      <span
        role="img"
        aria-label={name}
        className={className}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          height: tile,
          width: tile,
          borderRadius: "var(--radius-md)",
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: Math.round(tile * 0.5),
          lineHeight: 1,
          ...(variant === "mono"
            ? { color: "currentColor", boxShadow: "inset 0 0 0 2px currentColor" }
            : {
                color: "var(--color-on-accent)",
                background: "var(--brand-gradient)",
                boxShadow: "0 6px 16px color-mix(in srgb, var(--color-brand-teal-700) 32%, transparent), inset 0 1px 0 rgba(255,255,255,0.25)",
              }),
          ...style,
        }}
      >
        <span aria-hidden>{letter}</span>
      </span>
    );
  }

  if (variant === "gradient") {
    return (
      <img
        src={src}
        alt={name}
        height={size}
        width={width}
        className={className}
        style={{ display: "block", height: size, width: uploaded ? "auto" : width, maxWidth: size * 2, objectFit: "contain", ...style }}
        draggable={false}
      />
    );
  }

  if (variant === "boxed") {
    const tile = Math.round(size * 1.1);
    const inner = Math.round(size * 0.7);
    return (
      <span
        role="img"
        aria-label={name}
        className={className}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          height: tile,
          width: tile,
          borderRadius: "var(--radius-md)",
          background: "var(--brand-gradient)",
          boxShadow: "0 6px 16px color-mix(in srgb, var(--color-brand-teal-700) 32%, transparent), inset 0 1px 0 rgba(255,255,255,0.25)",
          ...style,
        }}
      >
        <span
          aria-hidden
          style={{
            display: "inline-block",
            height: inner,
            width: uploaded ? inner : Math.round(inner * ASPECT),
            backgroundColor: "var(--color-on-accent)",
            ...maskStyle(src),
          }}
        />
      </span>
    );
  }

  return (
    <span
      role="img"
      aria-label={name}
      className={className}
      style={{
        display: "inline-block",
        height: size,
        width,
        backgroundColor: "currentColor",
        ...maskStyle(src),
        ...style,
      }}
    />
  );
}
